import * as React from 'react'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { FaIcon, faClose } from '@/lib/fa'
import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { FieldsetSelect, Option } from '@/modules/settings/components/FieldsetSelect'
import { useQueryCompanies } from '@/modules/shared/query-client/useQueryCompanies'
import { useMutationMoveDatabase } from '@/lib/api/company/useCompanyDatabaseHooks'

interface MoveDatabaseModalProps {
  isOpen: boolean
  close: () => void
  companyId: string
  databaseId: number
  databaseName: string
}

const MoveDatabaseModal: React.FC<MoveDatabaseModalProps> = ({
  isOpen,
  close,
  companyId,
  databaseId,
  databaseName,
}) => {
  const { t } = useLocalize()
  const loading = useLoadingStore(selectLoading)
  const startLoading = useLoadingStore((s) => s.startLoading)
  const { data: companies = [] } = useQueryCompanies({ includeDeleted: false })
  const moveMutation = useMutationMoveDatabase(companyId)
  const [selectedCompanyId, setSelectedCompanyId] = React.useState<string>('')
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (isOpen) {
      setSelectedCompanyId('')
      setError(null)
    }
  }, [isOpen])

  const companyOptions: Option[] = React.useMemo(() => {
    return companies
      .filter((c) => c.id !== companyId && c.isActive !== false && c.deletedDate == null)
      .map((c) => ({
        label: c.companyName || c.uniqueIdentifier || String(c.id),
        value: String(c.id),
      }))
  }, [companies, companyId])

  const handleConfirm = async () => {
    setError(null)
    if (!selectedCompanyId) {
      setError(t('administrationPanel-companies.database-move-select-company'))
      return
    }

    const stopLoading = startLoading()
    try {
      await moveMutation.mutateAsync({
        sourceCompanyId: companyId,
        databaseId,
        destinationCompanyId: selectedCompanyId,
      })
      close()
    } catch (err: any) {
      const errorCode = err?.response?.data?.code
      let message =
        err?.response?.data?.message ||
        err?.message ||
        t('administrationPanel-companies.database-move-error')

      if (errorCode === 'CANNOT_MOVE_ACTIVE_DATABASE') {
        message = t('administrationPanel-companies.database-cannot-move-active')
      } else if (errorCode === 'DATABASE_ALREADY_EXISTS') {
        message = t('administrationPanel-companies.database-already-in-destination')
      }

      setError(message)
    } finally {
      stopLoading()
    }
  }

  if (!isOpen) return null

  return (
    <ModalLayout>
      <div className="tw-relative tw-flex tw-max-h-[90vh] tw-w-full tw-max-w-[500px] tw-flex-col tw-rounded-lg tw-bg-white">
        {loading && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-[200] tw-grid tw-select-none tw-place-items-center tw-bg-black/40">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        )}

        {/* Header */}
        <div className="tw-border-b tw-border-base-300 tw-px-4 tw-py-4">
          <div className="tw-flex tw-items-center tw-justify-between">
            <p className="tw-text-xl tw-font-semibold tw-text-content-base">
              {t('administrationPanel-companies.database-move-title')}
            </p>
            <FaIcon
              icon={faClose}
              className="tw-h-5 tw-w-5 tw-cursor-pointer"
              onClick={close}
            />
          </div>
        </div>

        {/* Content */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-6">
          <div className="tw-space-y-4">
            <div className="tw-rounded-lg tw-border tw-border-blue-200 tw-bg-blue-50 tw-p-4">
              <p className="tw-text-sm tw-text-blue-800">
                {t('administrationPanel-companies.database-name')}: <strong>{databaseName}</strong>
              </p>
            </div>

            <FieldsetSelect
              label={t('administrationPanel-companies.database-move-select-company')}
              placeholder={t('administrationPanel-companies.choose-company')}
              options={companyOptions}
              value={selectedCompanyId}
              searchEnabled
              handleChange={(value) => setSelectedCompanyId(value)}
              name="destinationCompanyId"
              required
            />

            {error && (
              <div className="tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-p-4">
                <p className="tw-text-sm tw-text-red-800">{error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="tw-border-t tw-border-base-300 tw-px-4 tw-py-4">
          <div className="tw-flex tw-justify-end tw-gap-3">
            <button
              type="button"
              onClick={close}
              disabled={loading}
              className="tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-100 disabled:tw-opacity-50"
            >
              {t('administrationPanel-companies.duplicate-database-cancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading || !selectedCompanyId}
              className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90 disabled:tw-opacity-50"
            >
              {t('administrationPanel-companies.database-move')}
            </button>
          </div>
        </div>
      </div>
    </ModalLayout>
  )
}

export default MoveDatabaseModal
